/**
 * KCIS Module 1 · Identity Admin Queue (super_admin only).
 * Route: /admin/identity
 *
 * Filterable submissions table + detail drawer (audit log, domain scores,
 * resend email, PDF download). Mirrors the AdminUniversity discipline:
 * route guard, auth header injection, no business-logic mixing.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Loader2, RefreshCw, Heart, Mail, Download, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { TopNav } from "@/components/Layout";
import { adminAuth } from "@/lib/api";
import { identityApi } from "@/lib/identityApi";
import { formatApiError } from "@/lib/formatError";

const ARCHETYPES = ["survival", "awakening", "rebuilding", "becoming"];
const BADGE = {
  survival: "bg-rose-50 text-rose-900 border-rose-200",
  awakening: "bg-amber-50 text-amber-800 border-amber-200",
  rebuilding: "bg-sky-50 text-sky-800 border-sky-200",
  becoming: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function AdminIdentity() {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [archetype, setArchetype] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!adminAuth.getToken()) {
      navigate("/admin/login");
      return;
    }
    if (!adminAuth.isSuperAdmin()) {
      toast.error("Super admin access required.");
      navigate("/admin");
      return;
    }
  }, [navigate]);

  const fetchHealth = async () => {
    try {
      const r = await identityApi.adminHealth();
      setHealth(r.data);
    } catch (err) {
      if ([401, 403].includes(err?.response?.status)) {
        adminAuth.clear();
        navigate("/admin/login");
      }
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await identityApi.adminList({ page, page_size: 25, archetype: archetype || undefined, q: q || undefined });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load submissions"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHealth(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchList(); }, [page, archetype]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (id) => {
    setOpenId(id);
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await identityApi.adminDetail(id);
      setDetail(r.data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load submission"));
    } finally {
      setDetailLoading(false);
    }
  };

  const resendEmail = async () => {
    if (!detail) return;
    setResending(true);
    try {
      const r = await identityApi.adminResendEmail(detail.id);
      toast.success(r?.data?.result?.status === "sent" ? "Email resent" : "Email queued");
      await openDetail(detail.id);
      await fetchHealth();
    } catch (err) {
      toast.error(formatApiError(err, "Resend failed"));
    } finally {
      setResending(false);
    }
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchList();
  };

  return (
    <div className="min-h-screen bg-[#faf7f5] text-slate-900" data-testid="admin-identity">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <Link to="/admin" className="text-xs text-slate-500 hover:text-purple-900 inline-flex items-center">
              <ChevronLeft className="w-3.5 h-3.5 mr-0.5" /> Back to admin
            </Link>
            <h1 className="mt-1 font-display text-3xl text-slate-900 flex items-center gap-3">
              <Heart className="w-6 h-6 text-purple-900" />
              Identity Alignment Submissions
            </h1>
            <p className="text-sm text-slate-600">KCIS Module 1 · Coaching & Consulting door</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { fetchHealth(); fetchList(); }} data-testid="ai-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Health tiles */}
        {health && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ai-health-tiles">
            <Tile label="Module" value={health.enabled ? "Enabled" : "Disabled"} accent={health.enabled ? "emerald" : "rose"} />
            <Tile label="Total" value={health.total_submissions} accent="purple" />
            <Tile label="Survival" value={health.by_archetype?.survival || 0} accent="rose" />
            <Tile label="Awakening" value={health.by_archetype?.awakening || 0} accent="amber" />
            <Tile label="Rebuilding" value={health.by_archetype?.rebuilding || 0} accent="sky" />
            <Tile label="Becoming" value={health.by_archetype?.becoming || 0} accent="emerald" />
          </div>
        )}

        {/* Filters */}
        <form onSubmit={onSearchSubmit} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-end gap-3 flex-wrap" data-testid="ai-filter-row">
          <div className="grow min-w-[200px]">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Search</div>
            <Input placeholder="name or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ai-search-input" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Archetype</div>
            <Select value={archetype || "all"} onValueChange={(v) => { setArchetype(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-44" data-testid="ai-archetype-filter">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {ARCHETYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" data-testid="ai-search-submit">
            <Search className="w-4 h-4 mr-1" /> Apply
          </Button>
        </form>

        {/* Queue table */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Submitted</TableHead>
                <TableHead>Applicant</TableHead>
                <TableHead>Archetype</TableHead>
                <TableHead>Identity Score</TableHead>
                <TableHead>Email Sent</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">No submissions match.</TableCell></TableRow>
              ) : items.map((row) => (
                <TableRow key={row.id} data-testid={`ai-row-${row.id}`}>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{row.created_at?.slice(0,16).replace("T", " ")}</TableCell>
                  <TableCell>
                    <div className="font-medium">{row.first_name}</div>
                    <div className="text-xs text-slate-500">{row.email}</div>
                    <div className="text-[11px] font-mono text-purple-900 mt-0.5">{row.id}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${BADGE[row.result_key] || ""} border`}>{row.result_key}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{row.score} / 72</TableCell>
                  <TableCell>
                    {row.email_sent ? <span className="text-emerald-700 text-xs">Sent</span> : <span className="text-slate-500 text-xs">Pending</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openDetail(row.id)} data-testid={`ai-open-${row.id}`}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between p-3 border-t border-slate-100 text-xs text-slate-500">
            <div>Total: {total}</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <div>Page {page}</div>
              <Button size="sm" variant="ghost" disabled={page * 25 >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        </div>
      </main>

      {/* Detail drawer */}
      <Sheet open={!!openId} onOpenChange={(o) => { if (!o) { setOpenId(null); setDetail(null); } }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="ai-detail-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-purple-900" />
              {detail?.first_name || "Loading…"}
              {detail?.result_key ? <Badge className={`${BADGE[detail.result_key]} border ml-2`}>{detail.result_key}</Badge> : null}
            </SheetTitle>
          </SheetHeader>
          {detailLoading || !detail ? (
            <div className="mt-6 text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading…</div>
          ) : (
            <div className="mt-6 space-y-6">
              <Section title="Identity">
                <KV k="Email" v={detail.email} />
                <KV k="Phone" v={detail.phone || "—"} />
                <KV k="Submission" v={<span className="font-mono text-xs">{detail.id}</span>} />
                <KV k="Submitted" v={detail.created_at?.replace("T", " ").slice(0, 16)} />
                <KV k="Identity Score" v={`${detail.score} / 72`} />
                <KV k="Email Sent" v={detail.email_sent ? "Yes" : "Pending"} />
                <KV k="PDF Generated" v={detail.pdf_generated ? "Yes" : "Pending"} />
              </Section>

              {detail.domain_narratives && (
                <Section title="Domain scores">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.values(detail.domain_narratives).map((d) => (
                      <div key={d.key} className="rounded-md border border-slate-200 p-2">
                        <div className="font-semibold">{d.label}</div>
                        <div className="text-slate-500">{d.score} / {d.max} · {d.band}</div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              <Section title="Actions">
                <div className="flex flex-wrap gap-2">
                  <Button asChild variant="outline" size="sm" data-testid="ai-download-pdf">
                    <a href={identityApi.adminPdfUrl(detail.id)} target="_blank" rel="noreferrer">
                      <Download className="w-4 h-4 mr-1" /> Download PDF
                    </a>
                  </Button>
                  <Button size="sm" disabled={resending} onClick={resendEmail} data-testid="ai-resend-email">
                    {resending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                    Resend email
                  </Button>
                </div>
              </Section>

              {detail.audit?.length > 0 && (
                <Section title="Audit log">
                  <ul className="text-xs space-y-2">
                    {detail.audit.map((a, i) => (
                      <li key={i} className="border-l-2 border-purple-200 pl-3">
                        <div className="font-mono">{a.action}</div>
                        <div className="text-slate-500">{a.at} · {a.actor}</div>
                        {a.reason && <div className="text-slate-600">{a.reason}</div>}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Tile({ label, value, accent = "purple" }) {
  const accentMap = {
    purple: "border-purple-200 bg-purple-50 text-purple-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    sky: "border-sky-200 bg-sky-50 text-sky-900",
  };
  return (
    <div className={`rounded-xl border p-3 ${accentMap[accent] || accentMap.purple}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-purple-900 font-semibold mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <div className="text-slate-500 min-w-[120px]">{k}</div>
      <div className="text-slate-900 break-words">{v}</div>
    </div>
  );
}
